import React, { useCallback, useEffect, useState, createElement } from 'react';
import { FileTextIcon, SearchIcon, PlusIcon, EditIcon, TrashIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, CalendarIcon, UserIcon, DollarSignIcon, ClipboardListIcon, PrinterIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { Textarea } from '../components/ui/Textarea';
import { supabaseAdmin } from '../lib/supabase';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
// Type definition locally since we haven't updated types/index.ts yet
// (It will be updated in the next step, but good to have here for safety)
export interface FacturaDejada {
  id: string;
  numeroFactura: string;
  farmacia: string;
  vendedor: string;
  vendedorId?: string;
  fechaDejada: Date;
  fechaRecogida?: Date | null;
  monto: number;
  estado: 'dejada' | 'recogida' | 'perdida';
  notas?: string;
}
type FormData = {
  numeroFactura: string;
  farmacia: string;
  vendedor: string;
  vendedorId: string;
  fechaDejada: string;
  fechaRecogida: string;
  monto: string;
  estado: FacturaDejada['estado'];
  notas: string;
};
const emptyForm: FormData = {
  numeroFactura: '',
  farmacia: '',
  vendedor: '',
  vendedorId: '',
  fechaDejada: new Date().toISOString().split('T')[0],
  fechaRecogida: '',
  monto: '',
  estado: 'dejada',
  notas: ''
};
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png';
async function getLogoBase64(): Promise<string | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = LOGO_URL;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || 100;
    canvas.height = img.naturalHeight || 100;
    canvas.getContext('2d')!.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
// ── Print "Detalle de Facturas Dejadas" receipt ──────────────────────────────
async function printFacturasDejadas(farmacia: string, vendedor: string, facturas: {
  numeroFactura: string;
  fecha: Date;
  monto: number;
}[]) {
  // 80mm thermal receipt format (same as CollectionsPage)
  const pw = 80;
  const ph = 220;
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [pw, ph]
  });
  const ml = 4;
  const mr = 4;
  const cw = pw - ml - mr; // 72mm usable
  const cx = pw / 2;
  const fmtCur = (n: number) => new Intl.NumberFormat('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(n);
  const fmtDate = (d: Date) => new Date(d).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
  const dashedLine = (y: number) => {
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.setLineDashPattern([1, 1], 0);
    doc.line(ml, y, pw - mr, y);
    doc.setLineDashPattern([], 0);
  };
  let y = 2;
  // ── LOGO ──────────────────────────────────────────────────────────────────
  const logoBase64 = await getLogoBase64();
  if (logoBase64) {
    const logoSize = 14;
    doc.addImage(logoBase64, 'PNG', cx - logoSize / 2, y, logoSize, logoSize);
    y += logoSize + 2;
  }
  // ── COMPANY HEADER ────────────────────────────────────────────────────────
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('MILADYS RODRIGUEZ & ASO.', cx, y, {
    align: 'center'
  });
  y += 5;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Santiago, R.D. • Tel. 809-247-2104', cx, y, {
    align: 'center'
  });
  y += 4;
  doc.text('RNC: 130-329291', cx, y, {
    align: 'center'
  });
  y += 5;
  dashedLine(y);
  y += 5;
  // ── RECEIPT NUMBER & DATE ─────────────────────────────────────────────────
  const receiptNum = Math.floor(Math.random() * 9000 + 1000).toString();
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('No: ' + receiptNum, ml, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Fecha: ' + fmtDate(new Date()), pw - mr, y, {
    align: 'right'
  });
  y += 6;
  // ── TITLE ─────────────────────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('DETALLE DE FACTURAS DEJADAS A:', cx, y, {
    align: 'center'
  });
  y += 6;
  dashedLine(y);
  y += 5;
  // ── CLIENT INFO ───────────────────────────────────────────────────────────
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('Nombre:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.text(farmacia, ml + 18, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text('Negocio:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.text(farmacia, ml + 18, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text('Vendedor:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.text(vendedor, ml + 20, y);
  y += 6;
  dashedLine(y);
  y += 3;
  // ── TABLE — 4 cols: FACTURA Nº | FECHA | VALOR | NETO ────────────────────
  const tCols = ['FACTURA Nº', 'FECHA', 'VALOR', 'NETO'];
  const tWidths = [20, 16, 18, 18]; // = 72
  const tRowH = 6;
  // Header row — white background with bold black text for thermal printer compatibility
  doc.setFillColor(255, 255, 255);
  doc.rect(ml, y, cw, tRowH, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.rect(ml, y, cw, tRowH, 'S');
  // Bottom border thicker for emphasis
  doc.setLineWidth(0.5);
  doc.line(ml, y + tRowH, ml + cw, y + tRowH);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  let tx = ml;
  tCols.forEach((col, i) => {
    doc.text(col, tx + tWidths[i] / 2, y + tRowH - 1.5, {
      align: 'center'
    });
    tx += tWidths[i];
  });
  y += tRowH;
  // Data rows
  const totalRows = Math.max(facturas.length, 6);
  let totalValor = 0;
  let totalNeto = 0;
  for (let i = 0; i < totalRows; i++) {
    const isEven = i % 2 === 0;
    doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 255);
    doc.rect(ml, y, cw, tRowH, 'F');
    doc.setDrawColor(200, 210, 220);
    doc.setLineWidth(0.15);
    doc.rect(ml, y, cw, tRowH, 'S');
    if (i < facturas.length) {
      const f = facturas[i];
      totalValor += f.monto;
      totalNeto += f.monto;
      let cx2 = ml;
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      // FACTURA Nº
      doc.text(f.numeroFactura, cx2 + tWidths[0] / 2, y + tRowH - 1.8, {
        align: 'center'
      });
      cx2 += tWidths[0];
      doc.setFont('helvetica', 'normal');
      // FECHA
      doc.text(fmtDate(f.fecha), cx2 + tWidths[1] / 2, y + tRowH - 1.8, {
        align: 'center'
      });
      cx2 += tWidths[1];
      // VALOR
      doc.text(fmtCur(f.monto), cx2 + tWidths[2] / 2, y + tRowH - 1.8, {
        align: 'center'
      });
      cx2 += tWidths[2];
      // NETO
      doc.setFont('helvetica', 'bold');
      doc.text(fmtCur(f.monto), cx2 + tWidths[3] / 2, y + tRowH - 1.8, {
        align: 'center'
      });
    }
    y += tRowH;
  }
  // TOTAL row
  doc.setFillColor(240, 245, 255);
  doc.rect(ml, y, cw, tRowH, 'F');
  doc.setDrawColor(13, 27, 70);
  doc.setLineWidth(0.3);
  doc.rect(ml, y, cw, tRowH, 'S');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  const totalStr = 'TOTAL: RD$ ' + fmtCur(totalNeto);
  doc.text(totalStr, ml + cw - 1.5, y + tRowH - 1.8, {
    align: 'right'
  });
  y += tRowH + 5;
  dashedLine(y);
  y += 8;
  // ── SIGNATURE LINES ───────────────────────────────────────────────────────
  const sigW = 28;
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.line(ml, y, ml + sigW, y);
  doc.line(pw - mr - sigW, y, pw - mr, y);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('ENTREGADO POR', ml + sigW / 2, y + 4, {
    align: 'center'
  });
  doc.text('RECIBIDO POR', pw - mr - sigW / 2, y + 4, {
    align: 'center'
  });
  y += 12;
  dashedLine(y);
  y += 5;
  // ── FOOTER ────────────────────────────────────────────────────────────────
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('--- Facturas Dejadas ---', cx, y, {
    align: 'center'
  });
  y += 7;
  // Barcode simulation
  const barY2 = y;
  const barH2 = 10;
  let bx2 = ml;
  const seed2 = receiptNum.split('').reduce((a: number, c: string) => a + c.charCodeAt(0), 0);
  for (let i = 0; i < 60; i++) {
    const w2 = seed2 * (i + 7) * 13 % 3 * 0.4 + 0.5;
    if (i % 2 === 0) {
      doc.setFillColor(0, 0, 0);
      doc.rect(bx2, barY2, w2, barH2, 'F');
    }
    bx2 += w2 + 0.3;
    if (bx2 > ml + cw - 2) break;
  }
  y += barH2 + 4;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('RECIBO: FD-' + receiptNum, cx, y, {
    align: 'center'
  });
  window.open(String(doc.output('bloburl')), '_blank');
}
export function FacturasDejadasPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const {
    clients,
    users
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  // Dynamic lists
  const FARMACIAS = clients.map(c => c.nombre).sort();
  const VENDEDORES = users.filter(u => u.activo && u.permisos?.['new-visit'] === true).map(u => u.nombre);
  const [invoices, setInvoices] = useState<FacturaDejada[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendedorFilter, setVendedorFilter] = useState('all');
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<FacturaDejada | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  // Map DB row to domain object
  const mapInvoice = (row: any): FacturaDejada => ({
    id: row.id,
    numeroFactura: row.numero_factura,
    farmacia: row.farmacia,
    vendedor: row.vendedor,
    vendedorId: row.vendedor_id || undefined,
    fechaDejada: new Date(row.fecha_dejada),
    fechaRecogida: row.fecha_recogida ? new Date(row.fecha_recogida) : null,
    monto: Number(row.monto),
    estado: row.estado,
    notas: row.notas
  });
  const loadInvoices = async () => {
    setIsLoading(true);
    try {
      const {
        data
      } = await supabaseAdmin.from('facturas_dejadas').select('id,numero_factura,farmacia,vendedor,vendedor_id,fecha_dejada,fecha_recogida,monto,estado,notas').order('fecha_dejada', {
        ascending: false
      }).limit(200);
      if (data) setInvoices(data.map(mapInvoice));
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    loadInvoices();
  }, []);
  const loadInvoicesCallback = useCallback(() => {
    loadInvoices();
  }, []);
  useRealtimeRefresh(['facturas_dejadas'], loadInvoicesCallback);
  // Filtering
  const filteredInvoices = invoices.filter(inv => {
    // Vendor-level filtering: non-admin users only see their own facturas
    if (!isAdmin && inv.vendedorId !== user?.id) return false;
    const matchesSearch = inv.farmacia.toLowerCase().includes(searchQuery.toLowerCase()) || inv.numeroFactura.toLowerCase().includes(searchQuery.toLowerCase()) || inv.vendedor.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inv.estado === statusFilter;
    const matchesVendedor = vendedorFilter === 'all' || inv.vendedor === vendedorFilter;
    return matchesSearch && matchesStatus && matchesVendedor;
  });
  // Sorting setup
  const {
    items: sortedInvoices,
    requestSort,
    sortConfig
  } = useSortableData(filteredInvoices, {
    numeroFactura: i => i.numeroFactura,
    farmacia: i => i.farmacia,
    vendedor: i => i.vendedor,
    fechaDejada: i => new Date(i.fechaDejada).getTime(),
    monto: i => i.monto,
    estado: i => i.estado
  });
  // Default sort by date descending if no sort config
  const finalInvoices = sortConfig ? sortedInvoices : [...sortedInvoices].sort((a, b) => new Date(b.fechaDejada).getTime() - new Date(a.fechaDejada).getTime());
  // Stats — use vendor-filtered data
  const myInvoices = isAdmin ? invoices : invoices.filter(i => i.vendedorId === user?.id);
  const totalDejadas = myInvoices.length;
  const pendientesRecoger = myInvoices.filter(i => i.estado === 'dejada').length;
  const montoPendiente = myInvoices.filter(i => i.estado === 'dejada').reduce((sum, i) => sum + i.monto, 0);
  const thisMonth = new Date().getMonth();
  const thisYear = new Date().getFullYear();
  const recogidasEsteMes = myInvoices.filter(i => {
    if (i.estado !== 'recogida' || !i.fechaRecogida) return false;
    return i.fechaRecogida.getMonth() === thisMonth && i.fechaRecogida.getFullYear() === thisYear;
  }).length;
  // Pagination
  const totalPages = Math.ceil(finalInvoices.length / ITEMS_PER_PAGE);
  const paginatedInvoices = finalInvoices.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // Formatters
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const getStatusBadge = (status: FacturaDejada['estado']) => {
    switch (status) {
      case 'recogida':
        return <Badge variant="success" data-id="element-2224">Depositada</Badge>;
      case 'perdida':
        return <Badge variant="danger" data-id="element-2225">Perdida</Badge>;
      default:
        return <Badge variant="warning" data-id="element-2226">Dejada</Badge>;
    }
  };
  // Actions
  const openAddModal = () => {
    setEditingInvoice(null);
    setFormError('');
    setFormData({
      ...emptyForm,
      // Default to current user if they are a salesperson
      vendedor: user?.rolNombre === 'Vendedor' ? user.nombre : VENDEDORES[0] || '',
      vendedorId: user?.rolNombre === 'Vendedor' ? user.id : users.find(u => u.activo)?.id || ''
    });
    setIsModalOpen(true);
  };
  const openEditModal = (invoice: FacturaDejada) => {
    setEditingInvoice(invoice);
    setFormData({
      numeroFactura: invoice.numeroFactura,
      farmacia: invoice.farmacia,
      vendedor: invoice.vendedor,
      vendedorId: invoice.vendedorId || '',
      fechaDejada: new Date(invoice.fechaDejada).toISOString().split('T')[0],
      fechaRecogida: invoice.fechaRecogida ? new Date(invoice.fechaRecogida).toISOString().split('T')[0] : '',
      monto: String(invoice.monto),
      estado: invoice.estado,
      notas: invoice.notas || ''
    });
    setFormError('');
    setIsModalOpen(true);
  };
  const handleSave = async () => {
    if (!formData.numeroFactura || !formData.farmacia || !formData.monto) {
      setFormError('Por favor completa los campos obligatorios.');
      return;
    }
    setIsSaving(true);
    setFormError('');
    try {
      const payload = {
        numero_factura: formData.numeroFactura,
        farmacia: formData.farmacia,
        vendedor: formData.vendedor,
        vendedor_id: formData.vendedorId || null,
        fecha_dejada: new Date(formData.fechaDejada).toISOString(),
        fecha_recogida: formData.fechaRecogida ? new Date(formData.fechaRecogida).toISOString() : null,
        monto: Number(formData.monto),
        estado: formData.estado,
        notas: formData.notas || ''
      };
      if (editingInvoice) {
        const {
          data,
          error
        } = await supabaseAdmin.from('facturas_dejadas').update(payload).eq('id', editingInvoice.id).select().single();
        if (error) {
          console.error('Supabase update error:', error);
          setFormError('Error al actualizar: ' + (error.message || 'Error desconocido. Verifica que la tabla facturas_dejadas exista en Supabase.'));
          setIsSaving(false);
          return;
        }
        if (data) {
          setInvoices(prev => prev.map(inv => inv.id === editingInvoice.id ? mapInvoice(data) : inv));
        }
        addActivityLog('Editó factura dejada', `Factura: ${formData.numeroFactura} | Farmacia: ${formData.farmacia}`);
      } else {
        const {
          data,
          error
        } = await supabaseAdmin.from('facturas_dejadas').insert(payload).select().single();
        if (error) {
          console.error('Supabase insert error:', error);
          setFormError('Error al guardar: ' + (error.message || 'Error desconocido. Verifica que la tabla facturas_dejadas exista en Supabase.'));
          setIsSaving(false);
          return;
        }
        if (data) {
          setInvoices(prev => [mapInvoice(data), ...prev]);
        }
        addActivityLog('Creó factura dejada', `Factura: ${formData.numeroFactura} | Farmacia: ${formData.farmacia} | Monto: RD${formData.monto}`);
      }
      setIsModalOpen(false);
    } catch (error: any) {
      console.error('Unexpected error:', error);
      setFormError('Error inesperado: ' + (error?.message || 'Intente nuevamente.'));
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingInvoice) return;
    setIsSaving(true);
    try {
      addActivityLog('Eliminó factura dejada', `Factura: ${editingInvoice.numeroFactura} | Farmacia: ${editingInvoice.farmacia}`);
      await supabaseAdmin.from('facturas_dejadas').delete().eq('id', editingInvoice.id);
      setInvoices(prev => prev.filter(inv => inv.id !== editingInvoice.id));
      setIsDeleteModalOpen(false);
      setIsModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };
  const markAsRecogida = async (invoice: FacturaDejada) => {
    try {
      const {
        data
      } = await supabaseAdmin.from('facturas_dejadas').update({
        estado: 'recogida',
        fecha_recogida: new Date().toISOString()
      }).eq('id', invoice.id).select().single();
      if (data) {
        setInvoices(prev => prev.map(inv => inv.id === invoice.id ? mapInvoice(data) : inv));
        addActivityLog('Marcó factura como recogida', `Factura: ${invoice.numeroFactura} | Farmacia: ${invoice.farmacia}`);
      }
    } catch (error) {
      console.error('Error updating status', error);
    }
  };
  const handlePrint = async (invoice: FacturaDejada) => {
    setPrintingId(invoice.id);
    try {
      // Group all facturas dejadas for the same farmacia on the same date
      const sameFarmacia = invoices.filter(inv => inv.farmacia === invoice.farmacia && new Date(inv.fechaDejada).toDateString() === new Date(invoice.fechaDejada).toDateString());
      await printFacturasDejadas(invoice.farmacia, invoice.vendedor, sameFarmacia.map(inv => ({
        numeroFactura: inv.numeroFactura,
        fecha: inv.fechaDejada,
        monto: inv.monto
      })));
    } finally {
      setPrintingId(null);
    }
  };
  return <div className="space-y-6" data-id="element-2227">
      <PageHeader title="Facturas Depositadas" subtitle="Control de facturas físicas depositadas de farmacias" icon={<FileTextIcon className="w-6 h-6" data-id="element-2229" />} data-id="element-2228">
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2231" />} onClick={openAddModal} data-id="element-2230">
          Nueva Factura Depositada
        </Button>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-2232">
        <StatsCard title="Total Dejadas" value={totalDejadas} icon={<FileTextIcon className="w-5 h-5" data-id="element-2234" />} variant="primary" data-id="element-2233" />
        <StatsCard title="Pendientes de Recoger" value={pendientesRecoger} icon={<ClockIcon className="w-5 h-5" data-id="element-2236" />} variant="warning" data-id="element-2235" />
        <StatsCard title="Monto Pendiente" value={formatCurrency(montoPendiente)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-2238" />} variant="danger" data-id="element-2237" />
        <StatsCard title="Depositadas este Mes" value={recogidasEsteMes} icon={<CheckCircleIcon className="w-5 h-5" data-id="element-2240" />} variant="success" data-id="element-2239" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 animate-stagger-2" data-id="element-2241">
        <div className="relative flex-1" data-id="element-2242">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2243" />
          <input type="text" placeholder="Buscar por farmacia, factura o vendedor..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-2244" />
        </div>
        <select value={vendedorFilter} onChange={e => setVendedorFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-2245">
          <option value="all" data-id="element-2246">Todos los vendedores</option>
          {VENDEDORES.map(v => <option key={v} value={v} data-id="element-2247">
              {v}
            </option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-2248">
          <option value="all" data-id="element-2249">Todos los estados</option>
          <option value="dejada" data-id="element-2250">Dejada (Pendiente)</option>
          <option value="recogida" data-id="element-2251">Depositada</option>
          <option value="perdida" data-id="element-2252">Perdida</option>
        </select>
      </div>

      {/* Table */}
      <Card padding="none" className="animate-stagger-3" data-id="element-2253">
        {filteredInvoices.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-2254">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-2255">
              <ClipboardListIcon className="w-8 h-8 text-slate-400" data-id="element-2256" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-2257">
              No hay facturas depositadas
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-2258">
              No se encontraron registros con los filtros aplicados
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-2259">
            <table className="w-full" data-id="element-2260">
              <thead data-id="element-2261">
                <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-2262">
                  <SortableTableHeader label="Factura" sortKey="numeroFactura" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2263" />
                  <SortableTableHeader label="Farmacia" sortKey="farmacia" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2264" />
                  <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2265" />
                  <SortableTableHeader label="Fecha Dejada" sortKey="fechaDejada" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2266" />
                  <SortableTableHeader label="Monto" sortKey="monto" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2267" />
                  <SortableTableHeader label="Estado" sortKey="estado" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2268" />
                  <th className="px-5 py-4 w-28 text-center text-xs font-semibold text-slate-500 uppercase font-heading" data-id="element-2269">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2270">
                {paginatedInvoices.map(invoice => <tr key={invoice.id} className={`hover:bg-slate-50/50 transition-colors ${invoice.estado === 'perdida' ? 'bg-rose-50/30' : ''}`} data-id="element-2271">
                    <td className="px-5 py-4 font-medium text-slate-900" data-id="element-2272">
                      {invoice.numeroFactura}
                    </td>
                    <td className="px-5 py-4 font-medium text-slate-900" data-id="element-2273">
                      {invoice.farmacia}
                    </td>
                    <td className="px-5 py-4" data-id="element-2274">
                      <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-2275">
                        <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2276" />
                        {invoice.vendedor}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600" data-id="element-2277">
                      {formatDate(invoice.fechaDejada)}
                      {invoice.fechaRecogida && <div className="text-xs text-emerald-600 mt-0.5" data-id="element-2278">
                          Depositada: {formatDate(invoice.fechaRecogida)}
                        </div>}
                    </td>
                    <td className="px-5 py-4 text-right font-bold text-slate-900" data-id="element-2279">
                      {formatCurrency(invoice.monto)}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-2280">
                      {getStatusBadge(invoice.estado)}
                    </td>
                    <td className="px-5 py-4" data-id="element-2281">
                      <div className="flex items-center justify-center gap-1" data-id="element-2282">
                        <button onClick={() => handlePrint(invoice)} title="Imprimir recibo" disabled={printingId === invoice.id} className="p-1.5 text-slate-400 hover:text-[#2952C4] hover:bg-[#eef2fc] rounded-lg transition-colors disabled:opacity-40" data-id="element-2283">
                          {printingId === invoice.id ? <div className="w-4 h-4 border-2 border-[#aac1ef] border-t-[#2952C4] rounded-full animate-spin" data-id="element-2284" /> : <PrinterIcon className="w-4 h-4" data-id="element-2285" />}
                        </button>
                        {invoice.estado === 'dejada' && <button onClick={() => markAsRecogida(invoice)} title="Marcar como Depositada" className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" data-id="element-2286">
                            <CheckCircleIcon className="w-4 h-4" data-id="element-2287" />
                          </button>}
                        <button onClick={() => openEditModal(invoice)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-2288">
                          <EditIcon className="w-4 h-4" data-id="element-2289" />
                        </button>
                        <button onClick={() => {
                    setEditingInvoice(invoice);
                    setIsDeleteModalOpen(true);
                  }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-2290">
                          <TrashIcon className="w-4 h-4" data-id="element-2291" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>

      <div className="animate-stagger-4" data-id="element-2292">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredInvoices.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-2293" />
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingInvoice ? 'Editar Factura Depositada' : 'Nueva Factura Depositada'} size="lg" data-id="element-2294">
        <ModalBody data-id="element-2295">
          <div className="space-y-4" data-id="element-2296">
            {formError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-2297">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-2298" />
                {formError}
              </div>}

            <div className="grid grid-cols-2 gap-4" data-id="element-2299">
              <Input label="Número de Factura" value={formData.numeroFactura} onChange={e => setFormData({
              ...formData,
              numeroFactura: e.target.value
            })} placeholder="Ej: FAC-12345" data-id="element-2300" />
              <Input label="Monto (DOP)" type="number" value={formData.monto} onChange={e => setFormData({
              ...formData,
              monto: e.target.value
            })} placeholder="0.00" data-id="element-2301" />
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-2302">
              <SearchableSelect label="Farmacia" value={formData.farmacia} onChange={val => setFormData({
              ...formData,
              farmacia: val
            })} options={FARMACIAS} placeholder="Buscar farmacia..." data-id="element-2303" />
              <div data-id="element-2304">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2305">
                  Vendedor
                </label>
                <select value={formData.vendedorId} onChange={e => {
                const selectedUser = users.find(u => u.id === e.target.value);
                setFormData({
                  ...formData,
                  vendedorId: e.target.value,
                  vendedor: selectedUser?.nombre || ''
                });
              }} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2306">
                  <option value="" data-id="element-2307">Seleccionar vendedor...</option>
                  {users.filter(u => u.activo).map(u => <option key={u.id} value={u.id} data-id="element-2308">
                        {u.nombre}
                      </option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-2309">
              <Input label="Fecha Dejada" type="date" value={formData.fechaDejada} onChange={e => setFormData({
              ...formData,
              fechaDejada: e.target.value
            })} data-id="element-2310" />
              <div data-id="element-2311">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2312">
                  Estado
                </label>
                <select value={formData.estado} onChange={e => setFormData({
                ...formData,
                estado: e.target.value as any
              })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2313">
                  <option value="dejada" data-id="element-2314">Dejada (Pendiente)</option>
                  <option value="recogida" data-id="element-2315">Depositada</option>
                  <option value="perdida" data-id="element-2316">Perdida</option>
                </select>
              </div>
            </div>

            {formData.estado === 'recogida' && <div className="animate-in fade-in slide-in-from-top-2" data-id="element-2317">
                <Input label="Fecha Depositada" type="date" value={formData.fechaRecogida} onChange={e => setFormData({
              ...formData,
              fechaRecogida: e.target.value
            })} data-id="element-2318" />
              </div>}

            <Textarea label="Notas (Opcional)" value={formData.notas} onChange={e => setFormData({
            ...formData,
            notas: e.target.value
          })} placeholder="Detalles adicionales..." rows={2} data-id="element-2319" />
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2320">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-2321">
            Cancelar
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} data-id="element-2322">
            {editingInvoice ? 'Guardar Cambios' : 'Registrar Factura'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Registro" size="sm" data-id="element-2323">
        <ModalBody data-id="element-2324">
          <p className="text-slate-600" data-id="element-2325">
            ¿Estás seguro de que deseas eliminar el registro de la factura{' '}
            <strong data-id="element-2326">{editingInvoice?.numeroFactura}</strong>? Esta acción no se
            puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-2327">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-2328">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} isLoading={isSaving} data-id="element-2329">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}